import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemBanner } from "../../../components/UIToolkit/molecules/SystemBanner";
import type { SandboxRenderResult } from "../../../components/UIToolkit/Previews/SandboxedHtmlRenderer";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import type { AiPrintoutShift } from "../../../downloads/aiPrintout/contract";
import type { HarnessFailureKind } from "../../../downloads/aiPrintout/harnessProtocol";
import { CustomPrintoutMissingPeople } from "./CustomPrintoutMissingPeople";

/** What went wrong drawing the page, per harness failure. Exhaustive: a new kind needs its own words. */
const FAILURE_DESCRIPTION_KEY: Record<HarnessFailureKind, string> = {
	timeout: "printout.custom.ai.failed.timeout",
	"too-large": "printout.custom.ai.failed.too.large",
	syntax: "printout.custom.ai.failed.error",
	runtime: "printout.custom.ai.failed.error",
	"bad-output": "printout.custom.ai.failed.error",
	unsupported: "printout.custom.ai.failed.unsupported",
	"not-ready": "printout.custom.ai.failed.not.ready",
	navigated: "printout.custom.ai.failed.navigated",
};

type Props = {
	result: SandboxRenderResult;
	shifts: AiPrintoutShift[];
	/** Can design (not read-only): offered Fix with Annie. */
	canDesign: boolean;
	/** A new design could fix this (and Annie isn't busy). */
	canFix: boolean;
	onFix: () => void;
};

/** The headline names the cause: the page didn't draw, people are missing, or only screen-size rules leave it unchecked. */
const titleKeyOf = (isRenderError: boolean, missingCount: number): string => {
	if (isRenderError) return "printout.custom.ai.failed.title";
	return missingCount > 0 ? "printout.custom.ai.incomplete.title" : "printout.custom.ai.incomplete.media.title";
};

/** The step after the problem: Fix with Annie where it can help and the reader can design, else the Detailed printout. */
const nextStepKey = (canDesign: boolean, canFix: boolean): string => {
	if (!canDesign) return "printout.custom.ai.next.step.view";
	return canFix ? "printout.custom.ai.next.step.fix" : "printout.custom.ai.next.step.detailed";
};

/**
 * Why printing is off, when it is, and what to do next: the page couldn't be drawn, or it leaves people out —
 * named here, locally, so the head nurse sees exactly who would be missing from the paper. Fix with Annie
 * sends the failure into the design chat; the Detailed printout is always there meanwhile.
 */
export const CustomPrintoutStatusBanner: FC<Props> = ({ result, shifts, canDesign, canFix, onFix }) => {
	const { t } = useTranslation();
	if (result.status === "rendering" || (result.status === "rendered" && result.report.ok)) return null;
	const isRenderError = result.status === "failed";
	const missing = new Set(result.status === "rendered" ? result.report.missing : []);
	return (
		<SystemBanner
			dataTestId={isRenderError ? "custom-printout-render-error" : "custom-printout-completeness-banner"}
			tone="error"
			live
			title={t(titleKeyOf(isRenderError, missing.size))}
			descriptionComponent="div"
			description={
				<>
					{result.status === "failed" && t(FAILURE_DESCRIPTION_KEY[result.kind])}
					{missing.size > 0 && <CustomPrintoutMissingPeople missingShifts={shifts.filter((shift) => missing.has(shift.id))} />}
					{result.status === "rendered" && result.report.mediaRules && (
						<Box component="span" sx={{ display: "block" }}>
							{t("printout.custom.ai.incomplete.print.media")}
						</Box>
					)}
					<Box component="span" sx={{ display: "block", mt: 0.5 }}>
						{t(nextStepKey(canDesign, canFix))}
					</Box>
				</>
			}
			action={
				canDesign && canFix ? (
					<SystemButton
						data-testid="custom-printout-fix-button"
						buttonVariant="primary"
						size="small"
						noFullWidthMobile
						leadingIcon={<AutoAwesomeIcon />}
						onClick={onFix}
					>
						{t("printout.custom.ai.fix")}
					</SystemButton>
				) : undefined
			}
		/>
	);
};
